import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {OverlayPanel} from 'primeng/overlaypanel';
import {MessageService} from 'primeng/api';
import {EventEnum} from '@app/enums/event.enum';
import {NotificacionWeb, NotificacionWebSocket, TipoNotificacionWeb} from '@app/domain/notificacion-web';
import {NotificacionWebService} from '@app/service/notificacion-web.service';
import {SocketIoService} from '@app/service/socket-io.service';
import {NotificacionWebActionService} from '@app/service/notificacion-web-action.service';
import {ListasService} from '@app/service/listas.service';

@Component({
    selector: 'app-notification-indicator',
    templateUrl: './notification-indicator.component.html',
    styleUrls: ['./notification-indicator.component.scss']
})
export class NotificationIndicatorComponent implements OnInit, OnDestroy {

    @ViewChild('recentPanel') recentPanel: OverlayPanel;

    readonly recentPageSize = 10;
    readonly historyPageSize = 10;

    recentNotifications: NotificacionWeb[] = [];
    historyNotifications: NotificacionWeb[] = [];
    historyTotal = 0;
    historyPage = 0;
    historyVisible = false;
    historyDateFrom: Date;
    historyDateTo: Date;
    loadingRecent = false;
    loadingHistory = false;
    unopenedCount = 0;

    private readonly destroy$ = new Subject<void>();
    private readonly openingNotificationIds = new Set<number>();

    constructor(private notificationService: NotificacionWebService,
                private socketIoService: SocketIoService,
                private notificationActionService: NotificacionWebActionService,
                private messageService: MessageService,
                public listas: ListasService) {
    }

    ngOnInit(): void {
        this.refreshUnopenedCount();
        this.socketIoService.onEvent(EventEnum.NOTIFICATION)
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => this.handleSocketNotification(event));
    }

    openRecent(event: Event, panel: OverlayPanel): void {
        this.loadRecent();
        panel.toggle(event);
    }

    openHistory(panel: OverlayPanel): void {
        panel.hide();
        this.historyVisible = true;
        this.loadHistory(0);
    }

    onHistoryPageChange(event: {page?: number}): void {
        this.loadHistory(event.page || 0);
    }

    applyHistoryFilters(): void {
        if (this.historyDateFrom && this.historyDateTo && this.historyDateFrom > this.historyDateTo) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Filtro de fechas',
                detail: 'La fecha desde no puede ser posterior a la fecha hasta.'
            });
            return;
        }
        this.loadHistory(0);
    }

    clearHistoryFilters(): void {
        this.historyDateFrom = undefined;
        this.historyDateTo = undefined;
        this.loadHistory(0);
    }

    notificationIcon(code: TipoNotificacionWeb): string {
        switch (code) {
            case 'PEDIDO_CATALOGO':
                return 'pi pi-shopping-cart';
            case 'PEDIDO_MENU':
                return 'pi pi-book';
            default:
                return 'pi pi-bell';
        }
    }

    notificationShortTitle(notification: NotificacionWeb): string {
        return notification.titulo || 'Nueva notificación';
    }

    trackByNotificationId(index: number, notification: NotificacionWeb): number {
        return notification.id;
    }

    get badgeText(): string {
        return this.unopenedCount > 99 ? '+99' : this.unopenedCount.toString();
    }

    markAsOpened(notification: NotificacionWeb): void {
        if (notification.abierto) {
            this.showAlreadyOpenedMessage();
            return;
        }
        if (this.openingNotificationIds.has(notification.id)) {
            return;
        }
        this.openingNotificationIds.add(notification.id);
        this.notificationService.markAsOpened(notification.id)
            .pipe(finalize(() => this.openingNotificationIds.delete(notification.id)))
            .subscribe({
                next: response => {
                    if (!response.abiertaAhora) {
                        this.showAlreadyOpenedMessage();
                        return;
                    }
                    this.updateNotificationState(notification.id);
                    this.notificationActionService.execute(notification, () => this.closeNotificationPanels());
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadRecent(): void {
        this.loadingRecent = true;
        this.notificationService.findAll(0, this.recentPageSize).subscribe({
            next: page => {
                this.recentNotifications = page.content || [];
                this.unopenedCount = page.notificacionesSinAbrir || 0;
                this.loadingRecent = false;
            },
            error: () => this.loadingRecent = false
        });
    }

    private loadHistory(pageNumber: number): void {
        this.loadingHistory = true;
        this.notificationService.findHistory(
            pageNumber,
            this.historyPageSize,
            this.historyDateFrom,
            this.historyDateTo
        ).subscribe({
            next: page => {
                this.historyNotifications = page.content || [];
                this.historyTotal = page.totalElements || 0;
                this.historyPage = page.number || 0;
                this.unopenedCount = page.notificacionesSinAbrir || 0;
                this.loadingHistory = false;
            },
            error: () => this.loadingHistory = false
        });
    }

    private updateNotificationState(id: number): void {
        [...this.recentNotifications, ...this.historyNotifications]
            .filter(notification => notification.id === id)
            .forEach(notification => {
                notification.abierto = true;
            });
    }

    private handleSocketNotification(event: any): void {
        const notification = (event?.value || event) as NotificacionWebSocket;
        if (!notification) {
            return;
        }
        if (notification.idEmpresa == null || !notification.codigo) {
            return;
        }
        this.refreshUnopenedCount();
    }

    private closeNotificationPanels(): void {
        if (this.recentPanel) {
            this.recentPanel.hide();
        }
        this.historyVisible = false;
    }

    private showAlreadyOpenedMessage(): void {
        this.messageService.add({
            severity: 'info',
            summary: 'Notificación',
            detail: 'Esta notificación ya fue abierta.'
        });
    }

    private refreshUnopenedCount(): void {
        this.notificationService.countUnopened().subscribe({
            next: response => {
                this.unopenedCount = response.notificacionesSinAbrir || 0;
            }
        });
    }
}
